/**
 * Хук для управления Web Push подписками.
 *
 * Handles:
 *  - Service worker registration check
 *  - Browser permission state (default/granted/denied)
 *  - Текущая subscription
 *  - subscribe / unsubscribe (синхронизирует с backend)
 *
 * iOS особенности: web push работает только если PWA установлен на home
 * screen (Apple's требование с iOS 16.4). До установки `Notification` в
 * Safari = undefined → isSupported=false.
 */
import { useCallback, useEffect, useState } from "react";

import { fetchVapidPublicKey, subscribePush, unsubscribePush } from "@/api/dashboard";


/** base64url → Uint8Array (для applicationServerKey). */
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}


export type PushState =
  | { status: "loading" }
  | { status: "unsupported"; reason: string }
  | { status: "denied" }
  | { status: "default" }                                    // permission ещё не запрошен
  | { status: "subscribed"; subscription: PushSubscription };


export function usePushNotifications() {
  const [state, setState] = useState<PushState>({ status: "loading" });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!("serviceWorker" in navigator)) {
      setState({ status: "unsupported", reason: "Service Worker не поддерживается" });
      return;
    }
    if (!("PushManager" in window)) {
      setState({ status: "unsupported", reason: "Push API не поддерживается. На iPhone — установите дашборд на главный экран (Safari → Поделиться → На экран «Домой»)" });
      return;
    }
    if (!("Notification" in window)) {
      setState({ status: "unsupported", reason: "Notification API не поддерживается" });
      return;
    }
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) {
      setState({ status: "unsupported", reason: "Service Worker ещё не активен — обнови страницу" });
      return;
    }
    if (Notification.permission === "denied") {
      setState({ status: "denied" });
      return;
    }
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      setState({ status: "subscribed", subscription: sub });
    } else {
      setState({ status: "default" });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const subscribe = useCallback(async () => {
    setBusy(true);
    let stage = "init";
    try {
      stage = "service-worker-ready";
      const reg = await navigator.serviceWorker.ready;
      stage = "request-permission";
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        alert(`Permission: ${perm}. Откройте системные настройки сайта и разрешите Notifications.`);
        await refresh();
        return false;
      }
      stage = "fetch-vapid-key";
      const { key } = await fetchVapidPublicKey();
      stage = "push-manager-subscribe";
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
      stage = "post-to-backend";
      const subJson = sub.toJSON() as PushSubscriptionJSON;
      await subscribePush({
        ...subJson,
        user_agent: navigator.userAgent,
      });
      setState({ status: "subscribed", subscription: sub });
      return true;
    } catch (e) {
      const err = e as Error;
      console.error(`Push subscribe failed at stage [${stage}]:`, err);
      alert(`Не удалось подписаться (${stage}):\n${err.message}\n\nОткройте DevTools → Console, скиньте подробности.`);
      await refresh();
      return false;
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  const unsubscribe = useCallback(async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await unsubscribePush(sub.endpoint).catch(() => { /* backend ok with miss */ });
        await sub.unsubscribe();
      }
      setState({ status: "default" });
    } catch (e) {
      console.error("Push unsubscribe failed:", e);
    } finally {
      setBusy(false);
    }
  }, []);

  return { state, busy, subscribe, unsubscribe, refresh };
}
